import React, { useState } from 'react';
import { X, Save, Settings, Bell, Clock, AlertTriangle } from 'lucide-react';
import { Workflow } from '../types/workflow';

interface WorkflowSettingsProps {
  workflow: Workflow;
  onUpdate: (updates: Partial<Workflow>) => void;
  onClose: () => void;
}

export const WorkflowSettings: React.FC<WorkflowSettingsProps> = ({
  workflow,
  onUpdate,
  onClose
}) => {
  const [localWorkflow, setLocalWorkflow] = useState<Workflow>(workflow);

  const handleSave = () => {
    onUpdate(localWorkflow);
    onClose();
  };

  const updateWorkflow = (updates: Partial<Workflow>) => {
    setLocalWorkflow(prev => ({ ...prev, ...updates }));
  };

  const updateSettings = (updates: any) => {
    setLocalWorkflow(prev => ({
      ...prev,
      definition: {
        ...prev.definition,
        settings: { ...prev.definition.settings, ...updates }
      }
    }));
  };

  const updateNotifications = (updates: any) => {
    setLocalWorkflow(prev => ({
      ...prev,
      definition: {
        ...prev.definition,
        settings: {
          ...prev.definition.settings,
          notifications: { ...prev.definition.settings?.notifications, ...updates }
        }
      }
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-neutral-900 border border-neutral-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-neutral-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Settings className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">Workflow Settings</h2>
                <p className="text-neutral-400 text-sm">{workflow.name}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-neutral-400 hover:text-white rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-8">
            {/* General Settings */}
            <div>
              <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                <Settings className="w-5 h-5" />
                General Settings
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">
                    Workflow Name
                  </label>
                  <input
                    type="text"
                    value={localWorkflow.name}
                    onChange={(e) => updateWorkflow({ name: e.target.value })}
                    className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={localWorkflow.description || ''}
                    onChange={(e) => updateWorkflow({ description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Describe what this workflow does..."
                  />
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={localWorkflow.isActive}
                    onChange={(e) => updateWorkflow({ isActive: e.target.checked })}
                    className="w-4 h-4 text-blue-600 bg-neutral-800 border-neutral-700 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="isActive" className="text-sm text-neutral-300">
                    Enable workflow (allow automatic execution)
                  </label>
                </div>
              </div>
            </div>

            {/* Execution Settings */}
            <div>
              <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Execution Settings
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">
                    Max Execution Time (seconds)
                  </label>
                  <input
                    type="number"
                    value={localWorkflow.definition.settings?.maxExecutionTime || ''}
                    onChange={(e) => updateSettings({ maxExecutionTime: parseInt(e.target.value) || undefined })}
                    className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="No limit"
                    min="1"
                  />
                  <p className="text-xs text-neutral-400 mt-1">
                    Maximum time allowed for workflow execution
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">
                    Max Retries
                  </label>
                  <input
                    type="number"
                    value={localWorkflow.definition.settings?.maxRetries || ''}
                    onChange={(e) => updateSettings({ maxRetries: parseInt(e.target.value) || undefined })}
                    className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="3"
                    min="0"
                    max="10"
                  />
                  <p className="text-xs text-neutral-400 mt-1">
                    Number of times to retry failed workflow execution
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">
                    Error Handling
                  </label>
                  <select
                    value={localWorkflow.definition.settings?.errorHandling || 'stop'}
                    onChange={(e) => updateSettings({ errorHandling: e.target.value })}
                    className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="stop">Stop on Error</option>
                    <option value="continue">Continue on Error</option>
                    <option value="retry">Retry on Error</option>
                  </select>
                  <p className="text-xs text-neutral-400 mt-1">
                    How to handle errors during workflow execution
                  </p>
                </div>
              </div>
            </div>

            {/* Notification Settings */}
            <div>
              <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Notification Settings
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="onSuccess"
                    checked={localWorkflow.definition.settings?.notifications?.onSuccess || false}
                    onChange={(e) => updateNotifications({ onSuccess: e.target.checked })}
                    className="w-4 h-4 text-blue-600 bg-neutral-800 border-neutral-700 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="onSuccess" className="text-sm text-neutral-300">
                    Notify on successful execution
                  </label>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="onError"
                    checked={localWorkflow.definition.settings?.notifications?.onError || false}
                    onChange={(e) => updateNotifications({ onError: e.target.checked })}
                    className="w-4 h-4 text-blue-600 bg-neutral-800 border-neutral-700 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="onError" className="text-sm text-neutral-300">
                    Notify on execution errors
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-2">
                    Notification Recipients
                  </label>
                  <textarea
                    value={localWorkflow.definition.settings?.notifications?.recipients?.join(', ') || ''}
                    onChange={(e) => updateNotifications({ 
                      recipients: e.target.value.split(',').map(email => email.trim()).filter(Boolean)
                    })}
                    rows={2}
                    className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="email1@example.com, email2@example.com"
                  />
                  <p className="text-xs text-neutral-400 mt-1">
                    Comma-separated list of email addresses
                  </p>
                </div>
              </div>
            </div>

            {/* Variables */}
            <div>
              <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Workflow Variables
              </h3>
              
              <div className="space-y-4">
                {localWorkflow.definition.variables?.map((variable, index) => (
                  <div key={index} className="p-4 bg-neutral-800 rounded-lg">
                    <div className="grid grid-cols-2 gap-4 mb-3">
                      <div>
                        <label className="block text-xs text-neutral-400 mb-1">Name</label>
                        <input
                          type="text"
                          value={variable.name}
                          onChange={(e) => {
                            const newVariables = [...(localWorkflow.definition.variables || [])];
                            newVariables[index] = { ...variable, name: e.target.value };
                            setLocalWorkflow(prev => ({
                              ...prev,
                              definition: { ...prev.definition, variables: newVariables }
                            }));
                          }}
                          className="w-full px-2 py-1 bg-neutral-700 border border-neutral-600 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-neutral-400 mb-1">Type</label>
                        <select
                          value={variable.type}
                          onChange={(e) => {
                            const newVariables = [...(localWorkflow.definition.variables || [])];
                            newVariables[index] = { ...variable, type: e.target.value as any };
                            setLocalWorkflow(prev => ({
                              ...prev,
                              definition: { ...prev.definition, variables: newVariables }
                            }));
                          }}
                          className="w-full px-2 py-1 bg-neutral-700 border border-neutral-600 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="string">String</option>
                          <option value="number">Number</option>
                          <option value="boolean">Boolean</option>
                          <option value="object">Object</option>
                          <option value="array">Array</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <label className="block text-xs text-neutral-400 mb-1">Default Value</label>
                      <input
                        type="text"
                        value={variable.defaultValue || ''}
                        onChange={(e) => {
                          const newVariables = [...(localWorkflow.definition.variables || [])];
                          newVariables[index] = { ...variable, defaultValue: e.target.value };
                          setLocalWorkflow(prev => ({
                            ...prev,
                            definition: { ...prev.definition, variables: newVariables }
                          }));
                        }}
                        className="w-full px-2 py-1 bg-neutral-700 border border-neutral-600 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs text-neutral-400 mb-1">Description</label>
                      <input
                        type="text"
                        value={variable.description || ''}
                        onChange={(e) => {
                          const newVariables = [...(localWorkflow.definition.variables || [])];
                          newVariables[index] = { ...variable, description: e.target.value };
                          setLocalWorkflow(prev => ({
                            ...prev,
                            definition: { ...prev.definition, variables: newVariables }
                          }));
                        }}
                        className="w-full px-2 py-1 bg-neutral-700 border border-neutral-600 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="Variable description"
                      />
                    </div>
                  </div>
                )) || []}
                
                <button
                  onClick={() => {
                    const newVariable = {
                      name: `variable_${Date.now()}`,
                      type: 'string' as const,
                      defaultValue: '',
                      description: ''
                    };
                    setLocalWorkflow(prev => ({
                      ...prev,
                      definition: {
                        ...prev.definition,
                        variables: [...(prev.definition.variables || []), newVariable]
                      }
                    }));
                  }}
                  className="w-full p-3 border-2 border-dashed border-neutral-600 rounded-lg text-neutral-400 hover:border-blue-400 hover:text-blue-400 transition-colors"
                >
                  + Add Variable
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-neutral-800">
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-neutral-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
            >
              <Save className="w-4 h-4" />
              Save Settings
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}; 